import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { CardDto, CardSection } from '../../lib/types';
import { zoneId } from '../../lib/boardMove';
import { CardTile, type SetMonth } from './CardTile';

interface SectionDropZoneProps {
  streamId: number;
  section: CardSection;
  /** This section's cards, in sortOrder. */
  cards: CardDto[];
  canWrite: boolean;
  onOpenCard: (cardId: number) => void;
  onSetMonth?: SetMonth;
}

/** Section colour-coding: label bar + left border. Amber takes navy text (white fails contrast on yellow). */
const sectionTheme: Record<CardSection, { bar: string; border: string }> = {
  Working: { bar: 'bg-emerald-700 text-white', border: 'border-l-emerald-700' },
  Ready: { bar: 'bg-amber-400 text-atg-navy', border: 'border-l-amber-400' },
  Planning: { bar: 'bg-atg-blue text-white', border: 'border-l-atg-blue' },
  Backlog: { bar: 'bg-atg-navy text-white', border: 'border-l-atg-navy' },
};

export function SectionDropZone({ streamId, section, cards, canWrite, onOpenCard, onSetMonth }: SectionDropZoneProps) {
  const { setNodeRef, isOver, over, active } = useDroppable({ id: zoneId(streamId, section) });
  const ids = cards.map((c) => c.id);
  // Highlight when the pointer is over the zone itself or over one of its cards.
  const highlighted = !!active && (isOver || (over !== null && ids.includes(over.id as number)));
  const theme = sectionTheme[section];

  return (
    <div className={`bg-white border border-slate-200 rounded-md overflow-hidden border-l-[3px] ${theme.border}`}>
      <div className={`flex items-center justify-between px-2 py-1 ${theme.bar}`}>
        <div className="text-[11px] font-semibold uppercase tracking-wide">{section}</div>
        <span className="text-[11px] font-semibold opacity-80">{cards.length}</span>
      </div>
      <SortableContext id={zoneId(streamId, section)} items={ids} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`p-2 flex flex-col gap-2 min-h-12 ${cards.length > 0 ? 'pb-6' : ''} ${highlighted ? 'bg-atg-blue/5 ring-2 ring-inset ring-atg-blue/40' : ''}`}
        >
          {cards.length === 0 ? (
            <div className="min-h-10 border border-dashed border-slate-300 rounded" aria-hidden="true" />
          ) : (
            cards.map((card) => (
              <CardTile key={card.id} card={card} canWrite={canWrite} onOpen={onOpenCard} onSetMonth={onSetMonth} />
            ))
          )}
        </div>
      </SortableContext>
    </div>
  );
}
